import React, { memo } from 'react';
import Joyride, { Step, Locale, TooltipRenderProps } from 'react-joyride';
import './styles.css';

interface JoyrideWrapperProps {
  steps: Step[];
  run: boolean;
  stepIndex?: number;
  continuous?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  disableOverlayClose?: boolean;
  disableCloseOnEsc?: boolean;
  callback?: (data: any) => void;
}

const customLocale: Locale = {
  back: 'Back',
  close: 'Close',
  last: 'Finish',
  next: 'Next',
  open: 'Open the dialog',
  skip: 'Skip Tour'
};

const customStyles = {
  options: {
    arrowColor: '#fff',
    backgroundColor: '#fff',
    overlayColor: 'rgba(0, 0, 0, 0.6)',
    primaryColor: '#083147',
    textColor: '#333',
    width: undefined,
    zIndex: 99999
  },
  tooltip: {
    borderRadius: 8,
    fontSize: 14,
    padding: 20
  },
  tooltipContainer: {
    textAlign: 'left' as const
  },
  buttonNext: {
    backgroundColor: '#083147',
    borderRadius: 4,
    fontSize: 14,
    padding: '8px 20px',
    outline: 'none'
  },
  buttonBack: {
    color: '#083147',
    marginRight: 10,
    fontSize: 14,
    padding: '8px 20px'
  },
  buttonSkip: {
    color: '#828282',
    fontSize: 13,
    padding: '8px 16px'
  },
  buttonClose: {
    color: '#828282',
    padding: 4
  },
  beacon: {
    backgroundColor: '#FF2D55'
  },
  beaconInner: {
    backgroundColor: '#FF2D55'
  },
  beaconOuter: {
    backgroundColor: 'rgba(255, 45, 85, 0.2)',
    border: '2px solid #FF2D55'
  }
};

// Custom Tooltip Component
const CustomTooltip = ({
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  size
}: TooltipRenderProps) => {
  const progress = ((index + 1) / size) * 100;

  return (
    <div {...tooltipProps} className='react-joyride__tooltip'>
      {step.title && <div className='onboarding-step-title'>{step.title}</div>}
      <div className='onboarding-step-content'>{step.content}</div>

      {/* Progress Indicator */}
      <div className='onboarding-progress'>
        <span className='onboarding-progress-text'>
          {index + 1} of {size}
        </span>
        <div className='onboarding-progress-bar'>
          <div className='onboarding-progress-fill' style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Footer with buttons */}
      <div className='react-joyride__tooltip__footer' style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {!isLastStep && (
              <button {...skipProps} style={{ ...customStyles.buttonSkip }}>
                {skipProps.title}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {index > 0 && (
              <button {...backProps} style={{ ...customStyles.buttonBack }}>
                {backProps.title}
              </button>
            )}
            <button {...primaryProps} style={{ ...customStyles.buttonNext }}>
              {primaryProps.title}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const JoyrideWrapper: React.FC<JoyrideWrapperProps> = ({
  steps,
  run,
  stepIndex = 0,
  continuous = true,
  showProgress = true,
  showSkipButton = true,
  disableOverlayClose = true,
  disableCloseOnEsc = false,
  callback
}) => {
  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous={continuous}
      showProgress={showProgress}
      showSkipButton={showSkipButton}
      disableOverlayClose={disableOverlayClose}
      disableCloseOnEsc={disableCloseOnEsc}
      callback={callback}
      locale={customLocale}
      styles={customStyles}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        disableAnimation: false,
        hideArrow: false,
        offset: 10,
        styles: {
          floater: {
            filter: 'drop-shadow(0 4px 20px rgba(8, 49, 71, 0.15))'
          },
          arrow: {
            length: 8,
            spread: 12
          }
        },
        options: {
          preventOverflow: {
            boundariesElement: 'viewport',
            padding: 20
          }
        }
      }}
      scrollToFirstStep
      scrollOffset={100}
      disableScrolling={false}
      spotlightClicks={false}
      hideBackButton={false}
    />
  );
};

export default memo(JoyrideWrapper);
